import { type InferResponseType, unwrap } from "@nexxt/api-client";
import { filterQuery } from "@nexxt/app-kit";
import type { ListBcRecordsInput } from "@nexxt/contracts/party";
import { partyQueries } from "@nexxt/party-ui";
import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { api } from "../../api";

// Server state of the customers and vendors as Business Central has them (50-api/party.md §2.9, ADR-116): the ones
// that wait for an organization, the ones of an organization, and the waiting ones that could be its own.

const records = api.party["bc-records"];

type ListPage = InferResponseType<typeof records.$get, 200>;
export type BcRecord = ListPage["items"][number];

/** The filters of the list input (`listBcRecordsInput`), without the paging the query adds. */
export type BcRecordFilters = Pick<ListBcRecordsInput, "kind" | "q" | "organizationId" | "possibleFor"> & {
  waiting?: boolean | undefined;
};

const PAGE_SIZE = 50;

const keys = {
  all: [...partyQueries.all, "bc-records"] as const,
  lists: [...partyQueries.all, "bc-records", "list"] as const,
};

export const bcRecordQueries = {
  ...keys,

  list: (filters: BcRecordFilters) =>
    infiniteQueryOptions({
      queryKey: [...keys.lists, filters],
      queryFn: ({ pageParam, signal }) =>
        unwrap(
          records.$get(
            { query: { ...filterQuery(filters), page: String(pageParam), pageSize: String(PAGE_SIZE) } },
            { init: { signal } },
          ),
        ),
      initialPageParam: 1,
      getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    }),

  /** The records of one organization, or the waiting ones that could be its own: few, one page. */
  of: (filters: Pick<BcRecordFilters, "organizationId" | "possibleFor" | "kind">) =>
    queryOptions({
      queryKey: [...keys.lists, "of", filters],
      queryFn: async ({ signal }) =>
        (await unwrap(records.$get({ query: { ...filterQuery(filters), pageSize: "100" } }, { init: { signal } })))
          .items,
    }),
};
